import React, { FC, useCallback, useState } from 'react';
import { Button, Card, CardBody, CardHeader, FormFeedback, Input, InputGroup } from 'reactstrap';
import { JSONForm } from '../../components/json-form/JSONForm';
import { useTranslation } from '../../translations';
import { isLogicError } from '../../utils';
import { LogicError } from '../../utils/errorHandling';

interface VoucherComponentProps {
  readonly orderId: number;
  readonly onVoucherApplied: () => void;
}

export const VoucherComponent: FC<VoucherComponentProps> = ({ orderId, onVoucherApplied }) => {
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const { ts } = useTranslation();

  const handleError = useCallback((err: Error) => {
    if (isLogicError(err)) {
      const error = err.apiResponse.errors.logic.find(
        ({ code }) =>
          LogicError.CannotRedeemVoucherNegative === code ||
          LogicError.InvalidVoucher === code ||
          LogicError.VoucherCannotBeUsedWithOtherVouchers === code,
      );

      if (error) {
        setErrorMessage(error.message);
        return true;
      }
    }

    return false;
  }, []);

  const onSuccess = useCallback(() => {
    onVoucherApplied();
    setErrorMessage(undefined);
  }, [onVoucherApplied]);

  return (
    <Card className="mb-10">
      <CardHeader>{ts('voucher_component.voucher_to_redeem')}</CardHeader>
      <CardBody>
        <JSONForm
          method="post"
          onFailure={handleError}
          onSuccess={onSuccess}
          path={`/api/orders/${orderId}/vouchers/apply`}
        >
          <InputGroup>
            <Input id="voucherCode" invalid={!!errorMessage} name="code" />
            <Button id="voucherRedeem">{ts('voucher_component.redeem_voucher')}</Button>
            {errorMessage && <FormFeedback invalid>{errorMessage}</FormFeedback>}
          </InputGroup>
        </JSONForm>
      </CardBody>
    </Card>
  );
};
